import React, { useState, useRef, useEffect } from 'react';
import { Typography, Box, IconButton, Stack } from '@mui/material';
import PlayCircleOutlineIcon from '@mui/icons-material/PlayCircleOutline';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import CloseIcon from '@mui/icons-material/Close';
import { ISound } from '../../../types/global/helper';
import { useThemeContext } from '../../../hook/useThemeManager';
import { theme } from '../../../constants/theme';
import { FONT_ENUM } from '../../../types/global/fonts.types';

export interface AudioCardProps {
    soundData: ISound;
    isEditMode?: boolean;
    onDelete?: (sound: ISound) => void;
}

const WAVEFORM_BARS = 36;

const AudioCard: React.FC<AudioCardProps> = ({ soundData, isEditMode = false, onDelete }) => {
    const [isPlaying, setIsPlaying] = useState(false);
    const [currentTime, setCurrentTime] = useState(0);
    const [duration, setDuration] = useState(0);
    const [waveform, setWaveform] = useState<number[]>([]);
    const [, setIsScrubbing] = useState(false);
    const audioRef = useRef<HTMLAudioElement | null>(null);
    const waveformRef = useRef<HTMLDivElement | null>(null);
    const { themeStyle } = useThemeContext();

    // THEME COLORS
    const playedColor = theme[themeStyle].highlight;
    const unplayedColor = theme[themeStyle].secondaryBg;
    const playButtonBg = theme[themeStyle].highlight;
    const playButtonHover = theme[themeStyle].primary_orange || theme[themeStyle].highlight;
    const cardBg = theme[themeStyle].deep_grey;
    const textColor = theme[themeStyle].tag_white;
    const subTextColor = theme[themeStyle].grey;

    // Fetch and decode audio for waveform
    useEffect(() => {
        let isMounted = true;
        setWaveform([]);
        if (!soundData.sound) return;
        fetch(soundData.sound)
            .then(res => res.arrayBuffer())
            .then(arrayBuffer => {
                const audioContext = new (window.AudioContext || (window as any).webkitAudioContext)();
                return audioContext.decodeAudioData(arrayBuffer);
            })
            .then(audioBuffer => {
                if (!isMounted) return;
                const rawData = audioBuffer.getChannelData(0); // Use first channel
                const samples = WAVEFORM_BARS;
                const blockSize = Math.floor(rawData.length / samples);
                const waveformData: number[] = [];
                for (let i = 0; i < samples; i++) {
                    let sum = 0;
                    for (let j = 0; j < blockSize; j++) {
                        sum += Math.abs(rawData[i * blockSize + j]);
                    }
                    waveformData.push(sum / blockSize);
                }
                // Normalize
                const max = Math.max(...waveformData);
                setWaveform(waveformData.map(v => v / (max || 1)));
            })
            .catch(() => {
                // fallback: flat waveform
                setWaveform(Array(WAVEFORM_BARS).fill(0.5));
            });
        return () => {
            isMounted = false;
        };
    }, [soundData.sound]);

    const handlePlayPause = () => {
        if (!audioRef.current) return;
        if (isPlaying) {
            audioRef.current.pause();
        } else {
            audioRef.current.play();
        }
        setIsPlaying(!isPlaying);
    };

    const handleTimeUpdate = () => {
        if (audioRef.current) {
            setCurrentTime(audioRef.current.currentTime);
        }
    };

    const handleLoadedMetadata = () => {
        if (audioRef.current) {
            setDuration(audioRef.current.duration);
        }
    };

    const handleEnded = () => {
        setIsPlaying(false);
        setCurrentTime(0);
    };

    // Scrubbing
    const handleWaveformPointerDown = (e: React.PointerEvent<HTMLDivElement>) => {
        if (!audioRef.current || !waveformRef.current || !duration) return;
        setIsScrubbing(true);
        handleWaveformPointerMove(e);
        window.addEventListener('pointermove', handleWaveformPointerMove);
        window.addEventListener('pointerup', handleWaveformPointerUp);
    };

    const handleWaveformPointerMove = (e: PointerEvent | React.PointerEvent) => {
        if (!audioRef.current || !waveformRef.current || !duration) return;
        const rect = waveformRef.current.getBoundingClientRect();
        const x = (e as PointerEvent).clientX - rect.left;
        const percent = Math.max(0, Math.min(1, x / rect.width));
        audioRef.current.currentTime = percent * duration;
        setCurrentTime(audioRef.current.currentTime);
    };

    const handleWaveformPointerUp = () => {
        setIsScrubbing(false);
        window.removeEventListener('pointermove', handleWaveformPointerMove);
        window.removeEventListener('pointerup', handleWaveformPointerUp);
    };

    const formatTime = (time: number) => {
        const minutes = Math.floor(time / 60);
        const seconds = Math.floor(time % 60);
        return `${minutes}:${seconds.toString().padStart(2, '0')}`;
    };

    // Highlighted bars for progress
    const playedBars = duration ? Math.floor((currentTime / duration) * waveform.length) : 0;

    return (
        <Stack
            direction="row"
            alignItems="center"
            sx={{
                width: '100%',
                maxWidth: 420,
                borderRadius: 2,
                overflow: 'hidden',
                border: `1px solid ${theme[themeStyle].tag_white}`,
                backgroundColor: cardBg,
                position: 'relative',
                p: 2,
            }}>
            {/* Delete Button (Only in Edit Mode) */}
            {isEditMode && (
                <IconButton
                    onClick={() => onDelete && onDelete(soundData)}
                    sx={{
                        position: 'absolute',
                        top: 5,
                        right: 5,
                        backgroundColor: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        width: 24,
                        height: 24,
                        zIndex: 2,
                        '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.9)' },
                    }}>
                    <CloseIcon fontSize="small" />
                </IconButton>
            )}

            {/* Play/Pause Button with Thumbnail */}
            <Box
                sx={{
                    position: 'relative',
                    width: 56,
                    height: 56,
                    mr: 2,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                }}>
                {soundData.thumbnail && (
                    <Box
                        component="img"
                        src={soundData.thumbnail}
                        alt={soundData.title}
                        sx={{
                            position: 'absolute',
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            zIndex: 0,
                        }}
                    />
                )}
                <IconButton
                    onClick={handlePlayPause}
                    sx={{
                        width: 56,
                        height: 56,
                        background: playButtonBg,
                        color: 'white',
                        boxShadow: 2,
                        zIndex: 1,
                        '&:hover': { background: playButtonHover },
                        position: 'relative',
                    }}>
                    {isPlaying ? (
                        <PauseCircleOutlineIcon sx={{ fontSize: 40 }} />
                    ) : (
                        <PlayCircleOutlineIcon sx={{ fontSize: 40 }} />
                    )}
                </IconButton>
            </Box>

            {/* Waveform */}
            <Box
                ref={waveformRef}
                onPointerDown={handleWaveformPointerDown}
                sx={{
                    flex: 1,
                    height: 56,
                    display: 'flex',
                    alignItems: 'center',
                    cursor: duration ? 'pointer' : 'default',
                    userSelect: 'none',
                    position: 'relative',
                }}>
                <svg width="100%" height="56" viewBox={`0 0 ${waveform.length * 8} 56`} style={{ display: 'block' }}>
                    {waveform.map((h, i) => {
                        const barHeight = h * 48 + 8;
                        const y = 56 - barHeight;
                        const isPlayed = i < playedBars;
                        return (
                            <rect
                                key={i}
                                x={i * 8}
                                y={y}
                                width={6}
                                height={barHeight}
                                rx={3}
                                fill={isPlayed ? playedColor : unplayedColor}
                                opacity={isPlayed ? 1 : 0.7}
                            />
                        );
                    })}
                </svg>
            </Box>

            {/* Audio Element (Hidden) */}
            <audio
                ref={audioRef}
                src={soundData.sound}
                onTimeUpdate={handleTimeUpdate}
                onLoadedMetadata={handleLoadedMetadata}
                onEnded={handleEnded}
                style={{ display: 'none' }}
            />

            {/* Details */}
            <Box sx={{ minWidth: 90, ml: 2 }}>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_BOLD, fontSize: 14, color: textColor }}>
                    {soundData.title}
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 12, color: subTextColor }}>
                    {soundData.artist}
                </Typography>
                <Typography sx={{ fontFamily: FONT_ENUM.MAIN_MEDIUM, fontSize: 10, color: subTextColor, mt: 0.5 }}>
                    {formatTime(currentTime)} / {formatTime(duration)}
                </Typography>
            </Box>
        </Stack>
    );
};

export { AudioCard };
